import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getCurrentUser, login as apiLogin, register as apiRegister, logout as apiLogout } from '@/api/auth';
import { toast } from 'sonner';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [authError, setAuthError] = useState(null);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);

  const openAuthModal = useCallback(() => setIsAuthModalOpen(true), []);
  const closeAuthModal = useCallback(() => {
    setIsAuthModalOpen(false);
    setAuthError(null);
  }, []);

  const bootstrapAuth = useCallback(async () => {
    try {
      setIsLoading(true);
      const userData = await getCurrentUser();
      if (userData && userData.user_id) {
        setUser(userData);
        setIsAuthenticated(true);
      } else {
        setUser(null);
        setIsAuthenticated(false);
      }
    } catch (err) {
      // 401 is normal for unauthenticated / anonymous state on bootstrap
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    bootstrapAuth();
  }, [bootstrapAuth]);

  const login = useCallback(async ({ email, password }) => {
    try {
      setIsLoading(true);
      setAuthError(null);
      localStorage.removeItem('vasteon_project_id');
      localStorage.removeItem('vasteon_session_id');
      await apiLogin({ email, password });
      const userData = await getCurrentUser();
      setUser(userData);
      setIsAuthenticated(true);
      closeAuthModal();
      toast.success(`Welcome back, ${userData.display_name || userData.email}`);
      return userData;
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Invalid email or password';
      setAuthError(msg);
      toast.error(msg);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [closeAuthModal]);

  const register = useCallback(async ({ email, password, displayName }) => {
    try {
      setIsLoading(true);
      setAuthError(null);
      localStorage.removeItem('vasteon_project_id');
      localStorage.removeItem('vasteon_session_id');
      await apiRegister({ email, password, displayName });
      // Seamlessly log in newly registered user
      await apiLogin({ email, password });
      const userData = await getCurrentUser();
      setUser(userData);
      setIsAuthenticated(true);
      closeAuthModal();
      toast.success(`Account created! Welcome, ${userData.display_name || userData.email}`);
      return userData;
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Failed to create account';
      setAuthError(msg);
      toast.error(msg);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [closeAuthModal]);

  const logout = useCallback(async () => {
    try {
      setIsLoading(true);
      await apiLogout();
    } catch (_) {
      // Ignore network errors on logout
    } finally {
      setUser(null);
      setIsAuthenticated(false);
      setIsLoading(false);
      // Clean up active project and session references on identity termination
      localStorage.removeItem('vasteon_project_id');
      localStorage.removeItem('vasteon_session_id');
      toast.info('Signed out successfully');
    }
  }, []);

  const handleSessionExpired = useCallback(() => {
    setUser(null);
    setIsAuthenticated(false);
    localStorage.removeItem('vasteon_project_id');
    localStorage.removeItem('vasteon_session_id');
    toast.warn('Session expired. Please sign in again.');
  }, []);

  const value = {
    user,
    isAuthenticated,
    isLoading,
    authError,
    isAuthModalOpen,
    openAuthModal,
    closeAuthModal,
    bootstrapAuth,
    login,
    register,
    logout,
    handleSessionExpired
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);

  if (!context) {
    return {
      user: null,
      isAuthenticated: false,
      isLoading: false,
      authError: null,
      isAuthModalOpen: false,
      openAuthModal: () => {},
      closeAuthModal: () => {},
      bootstrapAuth: async () => {},
      login: async () => {},
      register: async () => {},
      logout: async () => {},
      handleSessionExpired: () => {}
    };
  }

  return context;
};

export default AuthContext;
